/**
 * AcceptInvite — public page for invitees to set their password and log in.
 *
 * URL: /accept-invite?token=<raw_invitation_token>
 *
 * Flow:
 *   1. On mount, GET /api/auth-new/invitation/{token} → masked email + role.
 *   2. Render set-password form (with role banner + strength feedback).
 *   3. On submit, POST .../accept → cookies are set, user is logged in.
 *   4. Redirect to /admin/dashboard.
 *
 * Error states:
 *   - 410 Gone (expired/used/revoked) → friendly "link no longer valid" screen
 *     with a link to /forgot-password (in case they meant a reset).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { landingFor } from '@/lib/rolePath';
import {
  Button,
  Input,
  Card,
  CardContent,
  GradientText,
  ParticleBackground,
  Spinner,
} from '@/components/common';
import { Lock, ShieldCheck, AlertTriangle, ArrowLeft, CheckCircle2 } from 'lucide-react';

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'ready'; emailMasked: string; role: 'admin' | 'partner' | 'user' }
  | { kind: 'submitting'; emailMasked: string; role: 'admin' | 'partner' | 'user' }
  | { kind: 'success' };

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setPhase({ kind: 'invalid', message: 'no invitation token provided' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await authApi.invitationStatus(token);
        if (cancelled) return;
        if (!status.valid) {
          setPhase({ kind: 'invalid', message: 'invitation is no longer valid' });
        } else {
          setPhase({
            kind: 'ready',
            emailMasked: status.email_masked,
            role: status.role,
          });
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const detail = (err as any)?.response?.data?.detail || 'invitation is no longer valid';
        setPhase({ kind: 'invalid', message: typeof detail === 'string' ? detail : 'link expired' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Inline policy preview (mirrors backend ⚙7)
  const policyChecks = [
    { label: 'at least 12 characters', ok: password.length >= 12 },
    { label: 'one uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'one lowercase letter', ok: /[a-z]/.test(password) },
    { label: 'one digit', ok: /[0-9]/.test(password) },
    { label: 'one symbol', ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const policyOk = policyChecks.every((c) => c.ok);
  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (phase.kind !== 'ready') return;
    setFormError(null);

    if (!policyOk) {
      setFormError('password does not meet the policy below');
      return;
    }
    if (!passwordsMatch) {
      setFormError('passwords do not match');
      return;
    }

    setPhase({ kind: 'submitting', emailMasked: phase.emailMasked, role: phase.role });
    try {
      await authApi.invitationAccept(token, password);
      // Cookies are set; refresh AuthContext from /me so subsequent navigation
      // sees the user as authenticated.
      await refresh();
      setPhase({ kind: 'success' });
      // Brief success display, then redirect.
      // Pick the role's own landing — phase.role is admin / partner / user.
      setTimeout(() => navigate(landingFor(phase.role), { replace: true }), 1200);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail;
      let msg = 'could not complete; please try again';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        msg = `password rejected: ${detail.errors.join('; ')}`;
      }
      setFormError(msg);
      setPhase({ kind: 'ready', emailMasked: phase.emailMasked, role: phase.role });
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <ParticleBackground particleCount={60} />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="absolute top-6 left-6 z-20"
      >
        <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </Link>
      </motion.div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 rounded-xl bg-primary-50 border border-primary/20">
                <ShieldCheck className="w-10 h-10 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <GradientText text="Accept Invitation" />
            </h1>
            <p className="text-gray-500">Set your password to activate your DDARP Manager account</p>
          </div>

          <Card variant="glass" padding="lg">
            <CardContent>
              {phase.kind === 'loading' && (
                <div className="text-center py-8">
                  <Spinner size="xl" />
                  <p className="mt-4 text-gray-500">Validating invitation…</p>
                </div>
              )}

              {phase.kind === 'invalid' && (
                <div className="text-center py-8">
                  <AlertTriangle className="mx-auto w-12 h-12 text-amber-500 mb-4" />
                  <p className="text-gray-900 font-medium mb-2">Invitation link is no longer valid</p>
                  <p className="text-gray-500 text-sm mb-6">{phase.message}</p>
                  <div className="space-y-3">
                    <Link to="/forgot-password" className="block">
                      <Button variant="primary" fullWidth>
                        Use forgot password instead
                      </Button>
                    </Link>
                    <p className="text-xs text-gray-500">
                      Or ask your admin to resend the invitation.
                    </p>
                  </div>
                </div>
              )}

              {(phase.kind === 'ready' || phase.kind === 'submitting') && (
                <>
                  {/* Role banner */}
                  <div
                    className={
                      'flex items-center gap-3 p-3 rounded-lg mb-6 border ' +
                      (phase.role === 'admin'
                        ? 'bg-primary-50 border-primary/30 text-primary'
                        : 'bg-blue-50 border-blue-300 text-blue-700')
                    }
                  >
                    <ShieldCheck size={20} />
                    <div className="text-sm">
                      Setting up <strong className="font-semibold">{phase.emailMasked}</strong> as{' '}
                      <strong className="font-semibold capitalize">{phase.role}</strong>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {formError && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600">
                        <AlertTriangle size={20} />
                        <span className="text-sm">{formError}</span>
                      </div>
                    )}

                    <Input
                      label="New Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Choose a strong password"
                      leftIcon={<Lock size={18} />}
                      fullWidth
                      required
                      autoComplete="new-password"
                      disabled={phase.kind === 'submitting'}
                    />

                    <Input
                      label="Confirm Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter the same password"
                      leftIcon={<Lock size={18} />}
                      fullWidth
                      required
                      autoComplete="new-password"
                      disabled={phase.kind === 'submitting'}
                    />

                    {/* Policy checklist */}
                    <ul className="space-y-1 text-xs">
                      {policyChecks.map((c) => (
                        <li
                          key={c.label}
                          className={c.ok ? 'text-emerald-600' : 'text-gray-500'}
                        >
                          {c.ok ? '✓' : '○'} {c.label}
                        </li>
                      ))}
                      <li className={passwordsMatch ? 'text-emerald-600' : 'text-gray-500'}>
                        {passwordsMatch ? '✓' : '○'} passwords match
                      </li>
                    </ul>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      isLoading={phase.kind === 'submitting'}
                      disabled={!policyOk || !passwordsMatch || phase.kind === 'submitting'}
                    >
                      Set Password & Sign In
                    </Button>
                  </form>
                </>
              )}

              {phase.kind === 'success' && (
                <div className="text-center py-8">
                  <CheckCircle2 className="mx-auto w-12 h-12 text-emerald-500 mb-4" />
                  <p className="text-gray-900 font-medium mb-2">Welcome to DDARP Manager</p>
                  <p className="text-gray-500 text-sm">Redirecting to dashboard…</p>
                </div>
              )}
            </CardContent>
          </Card>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-xs text-gray-600 mt-8"
          >
            &copy; 2014-2026 MadHat Media, Inc. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
